/**
 * useAppResume — 앱이 백그라운드에서 포그라운드로 복귀했을 때 callback 실행.
 *
 * 두 환경 통합:
 *   - Capacitor 네이티브: `App.addListener('appStateChange')` (isActive true)
 *   - 웹/PWA: `document.visibilitychange` (visibilityState === 'visible')
 *
 * 잠깐 알림 보고 돌아오는 케이스에서 매번 refetch 가 일어나 깜빡이는 것을 방지하기 위해
 * 백그라운드 체류 시간이 minBackgroundMs 이상일 때만 callback 을 호출한다.
 *
 * Source: docs/12 single data layer 정책. 글로벌 refetchOnWindowFocus 는 false 유지하고,
 * 페이지별로 화이트리스트 방식으로 명시 적용.
 */
import { useEffect, useRef } from "react";
import { isNative, nativeWarn } from "../native";

interface UseAppResumeOptions {
  /** 백그라운드 체류가 이 값(ms) 이상일 때만 callback 실행. 기본 30,000ms (30초). */
  minBackgroundMs?: number;
  /** false 면 listener 등록을 건너뛴다. 기본 true. */
  enabled?: boolean;
}

const DEFAULT_MIN_BACKGROUND_MS = 30_000;

export function useAppResume(
  onResume: () => void,
  options: UseAppResumeOptions = {},
): void {
  const { minBackgroundMs = DEFAULT_MIN_BACKGROUND_MS, enabled = true } = options;

  const onResumeRef = useRef(onResume);
  onResumeRef.current = onResume;

  const backgroundedAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled) return;

    const fireIfStale = () => {
      const enteredAt = backgroundedAtRef.current;
      backgroundedAtRef.current = null;
      if (enteredAt === null) return;
      if (Date.now() - enteredAt < minBackgroundMs) return;
      onResumeRef.current();
    };

    // 웹: visibilitychange — Capacitor 웹뷰에서도 함께 발생하지만 중복 호출은
    // backgroundedAtRef 가 null 로 비워져 있으면 자동으로 무시된다.
    const handleVisibility = () => {
      if (document.visibilityState === "hidden") {
        if (backgroundedAtRef.current === null) {
          backgroundedAtRef.current = Date.now();
        }
      } else if (document.visibilityState === "visible") {
        fireIfStale();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);

    let nativeCleanup: (() => void) | null = null;

    if (isNative()) {
      (async () => {
        try {
          const { App } = await import("@capacitor/app");
          const handle = await App.addListener("appStateChange", (state) => {
            if (state.isActive) {
              fireIfStale();
            } else if (backgroundedAtRef.current === null) {
              backgroundedAtRef.current = Date.now();
            }
          });
          nativeCleanup = () => handle.remove();
        } catch (err) {
          nativeWarn("appResume:register", err);
        }
      })();
    }

    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      nativeCleanup?.();
    };
  }, [enabled, minBackgroundMs]);
}
